import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import supabase from "../../utils/supabase";
import {
  getConfirmPasswordValidation,
  getPasswordValidation,
} from "../../utils/yup";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ResetPasswordModal({
  isOpen,
  onClose,
  onSuccess,
}: ResetPasswordModalProps) {
  if (!isOpen) return null;

  const schemaShape = {
    userPassword: getPasswordValidation(),
    userConfirmPassword: getConfirmPasswordValidation(),
  };

  const schema = yup.object().shape(schemaShape, [
    ["userPassword", "userPassword"],
    ["userConfirmPassword", "userConfirmPassword"],
  ]);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Reset Password Required
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Because your account was created by a teacher with a weak password,
            it is required to create a new one.
          </p>
        </div>

        <Formik
          initialValues={{
            userPassword: "",
            userConfirmPassword: "",
          }}
          validationSchema={schema}
          onSubmit={async (values) => {
            const { data, error } = await supabase.auth.updateUser({
              password: values.userPassword,
              data: { forceResetPassword: false },
            });

            if (error) {
              toast.error(error.message);
              return;
            }

            if (data) {
              toast.success("Password reset successfully!");
              onSuccess();
            }
          }}
        >
          {(formik) => (
            <Form>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="userPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    New Password
                  </label>
                  <Field
                    type="password"
                    name="userPassword"
                    className="mt-1 block w-full h-10 border-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 outline-none"
                  />
                  {formik.errors.userPassword && (
                    <span className="text-red-500 text-sm">
                      {formik.errors.userPassword}
                    </span>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="userConfirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm New Password
                  </label>
                  <Field
                    type="password"
                    name="userConfirmPassword"
                    className="mt-1 block w-full h-10 border-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 outline-none"
                  />
                  {formik.errors.userConfirmPassword && (
                    <span className="text-red-500 text-sm">
                      {formik.errors.userConfirmPassword}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full py-2 px-4 rounded-md bg-indigo-500 text-white shadow-sm hover:bg-indigo-600 focus:ring focus:ring-indigo-300"
                >
                  Reset Password
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
